import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../components/AuthProvider';
import { StaticPageHeader } from '../components/StaticPageHeader';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { formatApiErrorDetail } from '../lib/apiError';

export const LoginPageRoute = () => {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Explicit redirect target (link click, ?next=, returning to a protected page).
  // Falls back to null so we can apply role-based defaults below.
  const explicitRedirect = location.state?.from || searchParams.get('next') || null;
  const sessionExpired = searchParams.get('reason') === 'session_expired';

  // Pick the right landing page for this user. Admins land on /admin by default;
  // everyone else on /members. An explicit `next` / `from` always wins so deep
  // links keep working (e.g. admin clicks a /members/library URL) — but for
  // admins, a generic `/members` redirect (set when ProtectedRoute bounces them
  // off the public "Member Portal" header link) is treated as no preference so
  // they still land on /admin.
  const destinationFor = (u) => {
    const isAdmin = u?.role === 'admin';
    if (explicitRedirect) {
      if (isAdmin) {
        const path = String(explicitRedirect).split('?')[0];
        if (path === '/members' || path === '/' || path === '') {
          return '/admin';
        }
      }
      return explicitRedirect;
    }
    return isAdmin ? '/admin' : '/members';
  };

  useEffect(() => {
    if (user) navigate(destinationFor(user), { replace: true });
  }, [user, navigate]);  // destinationFor uses stable refs (location/searchParams)

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const loggedInUser = await login(email.trim(), password);
      navigate(destinationFor(loggedInUser), { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <StaticPageHeader />
      <div className="flex-1 flex items-center justify-center pt-32 pb-16 px-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8" data-testid="login-card">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome back</h1>
          <p className="text-gray-600 mb-6">Sign in to your DriveHR member account.</p>
          {sessionExpired && (
            <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm" data-testid="session-expired-banner">
              You were signed out because your password was changed elsewhere. Please sign in again with your new password.
            </div>
          )}
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="login-error">
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" />
            </div>
            <Button type="submit" disabled={submitting} className="w-full bg-blue-600 hover:bg-blue-700 text-white" size="lg" data-testid="login-submit">
              {submitting ? 'Signing in...' : 'Sign in'}
            </Button>
          </form>
          <div className="mt-6 flex justify-between text-sm">
            <Link to="/forgot-password" className="text-blue-600 hover:text-blue-800" data-testid="forgot-link">Forgot password?</Link>
            <Link to="/signup" className="text-blue-600 hover:text-blue-800" data-testid="signup-link">Create account</Link>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export const SignupPageRoute = () => {
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tier = searchParams.get('tier') || '';
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      // After signup, send straight to checkout for the chosen tier (if any), else /members.
      navigate(tier ? `/checkout?tier=${tier}` : '/members', { replace: true });
    }
  }, [user, tier, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setSubmitting(true);
    try {
      await register({ email: email.trim(), password, first_name: firstName.trim(), last_name: lastName.trim() });
      navigate(tier ? `/checkout?tier=${tier}` : '/members', { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <StaticPageHeader />
      <div className="flex-1 flex items-center justify-center pt-32 pb-16 px-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8" data-testid="signup-card">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Create your account</h1>
          <p className="text-gray-600 mb-6">
            {tier ? <>Step 1 of 2 — you'll subscribe to the <span className="font-semibold capitalize">{tier}</span> plan next.</> : 'Join DriveHR to access your member portal.'}
          </p>
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="signup-error">{error}</div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="firstName">First name</Label>
                <Input id="firstName" required value={firstName} onChange={(e) => setFirstName(e.target.value)} data-testid="signup-first-name" />
              </div>
              <div>
                <Label htmlFor="lastName">Last name</Label>
                <Input id="lastName" required value={lastName} onChange={(e) => setLastName(e.target.value)} data-testid="signup-last-name" />
              </div>
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="signup-email" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" autoComplete="new-password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="signup-password" />
              <p className="text-xs text-gray-500 mt-1">At least 8 characters.</p>
            </div>
            <Button type="submit" disabled={submitting} className="w-full bg-blue-600 hover:bg-blue-700 text-white" size="lg" data-testid="signup-submit">
              {submitting ? 'Creating account...' : 'Create account'}
            </Button>
          </form>
          <p className="mt-6 text-sm text-center text-gray-600">
            Already have an account? <Link to="/login" className="text-blue-600 hover:text-blue-800">Sign in</Link>
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export const ForgotPasswordRoute = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setMessage(''); setSubmitting(true);
    try {
      const axios = (await import('axios')).default;
      const { data } = await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      setMessage(data.message);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <StaticPageHeader />
      <div className="flex-1 flex items-center justify-center pt-32 pb-16 px-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8" data-testid="forgot-card">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Reset your password</h1>
          <p className="text-gray-600 mb-6">Enter your email and we'll send you a reset link.</p>
          {error && <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}
          {message && <div className="mb-4 p-3 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm" data-testid="forgot-success">{message}</div>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" />
            </div>
            <Button type="submit" disabled={submitting} className="w-full bg-blue-600 hover:bg-blue-700 text-white" size="lg" data-testid="forgot-submit">
              {submitting ? 'Sending...' : 'Send reset link'}
            </Button>
          </form>
          <p className="mt-6 text-sm text-center text-gray-600">
            <Link to="/login" className="text-blue-600 hover:text-blue-800">Back to sign in</Link>
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export const ResetPasswordRoute = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setMessage('');
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match.'); return; }
    setSubmitting(true);
    try {
      const axios = (await import('axios')).default;
      const { data } = await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setMessage(data.message);
      setTimeout(() => navigate('/login'), 1500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <StaticPageHeader />
      <div className="flex-1 flex items-center justify-center pt-32 pb-16 px-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8" data-testid="reset-card">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Set a new password</h1>
          {!token && <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">Missing reset token. Use the link from your email.</div>}
          {error && <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}
          {message && <div className="mb-4 p-3 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm">{message}</div>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="password">New password</Label>
              <Input id="password" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password" />
            </div>
            <div>
              <Label htmlFor="confirmPassword">Confirm new password</Label>
              <Input id="confirmPassword" type="password" required minLength={8} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} data-testid="reset-confirm" />
            </div>
            <Button type="submit" disabled={submitting || !token} className="w-full bg-blue-600 hover:bg-blue-700 text-white" size="lg" data-testid="reset-submit">
              {submitting ? 'Updating...' : 'Update password'}
            </Button>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
};
